<script>
/**
 * CropFotoModal — recorte quadrado (estilo foto de perfil) sem dependência
 * externa. Renderiza a imagem num canvas, permite arrastar (pan) e dar zoom,
 * e exporta um Blob JPEG quadrado 640x640 pronto para upload.
 */
const STAGE = 300;   // tamanho do palco de preview (px)
const OUT = 640;     // tamanho de saída (px)

export default {
  name: 'CropFotoModal',
  props: {
    visivel: { type: Boolean, default: false },
    file: { type: [Object, null], default: null },
    // Estado do upload controlado pelo pai (para mostrar spinner/erro aqui).
    enviando: { type: Boolean, default: false },
    erroExterno: { type: [String, null], default: null },
  },
  emits: ['fechar', 'confirmar'],
  data() {
    return {
      STAGE,
      zoom: 1,
      carregando: false,
      processando: false,
      erro: null,
    };
  },
  computed: {
    ocupado() { return this.processando || this.enviando; },
    erroMostrar() { return this.erro || this.erroExterno; },
  },
  created() {
    // Estado não-reativo (o desenho é feito manualmente via _draw()).
    this.imgEl = null;
    this.coverScale = 1;
    this.offX = 0;
    this.offY = 0;
    this.drag = null;
    this.objUrl = null;
  },
  watch: {
    visivel(v) {
      if (v && this.file) this.$nextTick(() => this._carregarImagem());
      if (!v) this._limpar();
    },
    zoom() {
      this._clampOffset();
      this._draw();
    },
  },
  mounted() {
    if (this.visivel && this.file) this.$nextTick(() => this._carregarImagem());
  },
  beforeUnmount() {
    this._limpar();
  },
  methods: {
    _limpar() {
      if (this.objUrl) { URL.revokeObjectURL(this.objUrl); this.objUrl = null; }
      this.imgEl = null;
      this.drag = null;
    },
    _carregarImagem() {
      this.erro = null;
      this.carregando = true;
      this.zoom = 1;
      if (this.objUrl) URL.revokeObjectURL(this.objUrl);
      this.objUrl = URL.createObjectURL(this.file);
      const img = new Image();
      img.onload = () => {
        this.imgEl = img;
        this.coverScale = Math.max(STAGE / img.naturalWidth, STAGE / img.naturalHeight);
        // Centraliza
        const drawW = img.naturalWidth * this.coverScale;
        const drawH = img.naturalHeight * this.coverScale;
        this.offX = (STAGE - drawW) / 2;
        this.offY = (STAGE - drawH) / 2;
        this.carregando = false;
        this._draw();
      };
      img.onerror = () => {
        this.carregando = false;
        this.erro = 'Não foi possível carregar a imagem.';
      };
      img.src = this.objUrl;
    },
    _drawScale() {
      return this.coverScale * this.zoom;
    },
    _clampOffset() {
      if (!this.imgEl) return;
      const drawW = this.imgEl.naturalWidth * this._drawScale();
      const drawH = this.imgEl.naturalHeight * this._drawScale();
      // A imagem deve sempre cobrir o palco (sem bordas vazias).
      this.offX = Math.min(0, Math.max(STAGE - drawW, this.offX));
      this.offY = Math.min(0, Math.max(STAGE - drawH, this.offY));
    },
    _draw() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.imgEl) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, STAGE, STAGE);
      const drawW = this.imgEl.naturalWidth * this._drawScale();
      const drawH = this.imgEl.naturalHeight * this._drawScale();
      ctx.drawImage(this.imgEl, this.offX, this.offY, drawW, drawH);
    },
    onPointerDown(e) {
      if (!this.imgEl) return;
      const p = this._ponto(e);
      this.drag = { x: p.x, y: p.y, ox: this.offX, oy: this.offY };
      try { e.target.setPointerCapture(e.pointerId); } catch (_e) { /* noop */ }
    },
    onPointerMove(e) {
      if (!this.drag) return;
      const p = this._ponto(e);
      this.offX = this.drag.ox + (p.x - this.drag.x);
      this.offY = this.drag.oy + (p.y - this.drag.y);
      this._clampOffset();
      this._draw();
    },
    onPointerUp(e) {
      this.drag = null;
      try { e.target.releasePointerCapture(e.pointerId); } catch (_e) { /* noop */ }
    },
    _ponto(e) {
      const r = this.$refs.canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    },
    async confirmar() {
      if (!this.imgEl || this.ocupado) return;
      this.erro = null;
      this.processando = true;
      try {
        const out = document.createElement('canvas');
        out.width = OUT;
        out.height = OUT;
        const ctx = out.getContext('2d');
        const F = OUT / STAGE;
        const drawW = this.imgEl.naturalWidth * this._drawScale() * F;
        const drawH = this.imgEl.naturalHeight * this._drawScale() * F;
        ctx.drawImage(this.imgEl, this.offX * F, this.offY * F, drawW, drawH);
        const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.92));
        if (!blob) throw new Error('Falha ao gerar a imagem recortada.');
        this.$emit('confirmar', blob);
      } catch (error) {
        this.erro = error.message;
      } finally {
        this.processando = false;
      }
    },
    fechar() {
      if (this.ocupado) return;
      this.$emit('fechar');
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="visivel" class="modal-backdrop-custom" @click.self="fechar">
      <div class="modal-card shadow-lg">
        <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <h6 class="m-0"><i class="ri-crop-line me-1"></i>Recortar foto</h6>
          <button type="button" class="btn btn-link p-0 text-body" :disabled="processando" @click="fechar">
            <i class="ri-close-line" style="font-size: 1.4rem;"></i>
          </button>
        </header>

        <div class="modal-body-custom p-3 text-center">
          <div v-if="erroMostrar" class="alert alert-danger py-2 small text-start">{{ erroMostrar }}</div>

          <div class="crop-stage mx-auto" :style="{ width: STAGE + 'px', height: STAGE + 'px' }">
            <canvas
              ref="canvas"
              :width="STAGE"
              :height="STAGE"
              class="crop-canvas"
              @pointerdown="onPointerDown"
              @pointermove="onPointerMove"
              @pointerup="onPointerUp"
              @pointercancel="onPointerUp"
            ></canvas>
            <div class="crop-frame"></div>
            <div v-if="carregando" class="crop-loading">
              <i class="ri-loader-4-line spin"></i>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2 mt-3 px-2">
            <i class="ri-zoom-out-line text-muted"></i>
            <input v-model.number="zoom" type="range" min="1" max="3" step="0.01" class="form-range flex-grow-1" />
            <i class="ri-zoom-in-line text-muted"></i>
          </div>
          <small class="text-muted d-block mt-1">Arraste para posicionar e use o controle para dar zoom.</small>
        </div>

        <footer class="d-flex justify-content-end gap-2 px-3 py-2 border-top">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="ocupado" @click="fechar">Cancelar</button>
          <button type="button" class="btn btn-success btn-sm" :disabled="ocupado || carregando" @click="confirmar">
            <i v-if="ocupado" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-check-line me-1"></i>
            {{ ocupado ? 'Enviando...' : 'Recortar e enviar' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 380px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-body-custom { overflow-y: auto; flex: 1; }

.crop-stage {
  position: relative;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--vz-tertiary-bg, #f3f6f9);
  touch-action: none;
}
.crop-canvas {
  display: block;
  cursor: grab;
}
.crop-canvas:active { cursor: grabbing; }
/* Máscara circular indicando a área que vira a foto de perfil. */
.crop-frame {
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.35);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.85);
}
.crop-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: var(--vz-secondary-color, #878a99);
}
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
